<template>
  <v-container class="estimated-insurance-required">
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link
          :to="{ name: 'EstimatedInsuranceRequiredOutput' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="11">
        <h1 class="mb-2">How much insurance cover do you need?</h1>
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-sm-6">
      <v-col cols="12" sm="3" offset-sm="1" class="form-blurb">
        <v-img
          :src="
            require(`@/assets/${whitelabel.images.estimatedInsuranceRequiredPage}`)
          "
          aspect-ratio="1"
          alt="estimated insurance required"
          class="insurance-img"
          :class="{ 'insurance-img-xs': $vuetify.breakpoint.xs }"
        />
        <p class="mt-8">
          Consider what you have easily available like cash to cover your estate
          administration costs and amounts your estate needs for your
          beneficiaries and purchase insurance cover for the difference.
        </p>
      </v-col>

      <v-col cols="12" sm="6" offset-sm="1">
        <ValidationObserver ref="observer">
          <h3>How much cover do you need:</h3>
          <ul>
            <li>
              <v-row no-gutters>
                <v-col cols="12" sm="8" class="mt-2">
                  for estate administration expenses, paid out to your estate?
                </v-col>
                <v-col cols="12" sm="4">
                  <ValidationProvider
                    id="estate_admin_expenses_cover_required"
                    v-slot="{ errors }"
                    name="estate_admin_expenses_cover_required"
                    rules="required"
                  >
                    <v-text-field
                      v-model="currencyEstateAdminExpensesCoverRequired"
                      :error-messages="errors"
                      prefix="R"
                      type="text"
                      pattern="[0-9\s]*"
                      @keypress="isNumber($event)"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" sm="6">
                  <div class="primary-box">
                    <span v-if="estimatedCoverRequired"
                      >Your estimated costs amount to R
                      {{ estimatedCoverRequired | currency }}</span
                    >
                    <router-link
                      v-else
                      :to="{ name: 'EstimatedInsuranceRequiredInput' }"
                      >Calculate your estimated admin costs</router-link
                    >
                  </div>
                </v-col>
              </v-row>
            </li>
            <li>
              <v-row no-gutters>
                <v-col cols="12" sm="8" class="mt-2">
                  for my nominated beneficiary, paid out to the nominated
                  beneficiary?
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="currencyNominatedBeneficiaryCoverRequired"
                    prefix="R"
                    type="text"
                    pattern="[0-9\s]*"
                    @keypress="isNumber($event)"
                  />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" sm="6">
                  <div class="primary-box">
                    Paid out within 2 working days of receiving all necessary
                    documentation supporting a valid claim to the person you
                    choose to help cover immediate expenses like funeral costs,
                    household expenses, travel etc.
                  </div>
                </v-col>
              </v-row>
            </li>
          </ul>

          <v-row v-if="hasNominatedBeneficiaryCoverRequired" class="mt-6">
            <v-col cols="12">
              <h3 class="mb-2">Who will be your nominated beneficiary?</h3>
              <PeopleList
                :people="nominatedBeneficiaryArray"
                :testator="testator"
                relation="INSURANCE_NOMINATED_BENEFICIARY"
                :limit="1"
                class="nominated-beneficiary"
              />
              <ValidationProvider
                id="insurance_nominated_beneficiary"
                v-slot="{ errors }"
                name="insurance_nominated_beneficiary"
                rules="required"
              >
                <input
                  :value="peopleRelations.insuranceNominatedBeneficiary.id"
                  type="hidden"
                />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>
          </v-row>

          <v-row no-gutters class="mt-8">
            <v-col cols="12" sm="8">
              <h2>Total amount of cover</h2>
            </v-col>

            <v-col cols="12" sm="4">
              <div class="total-amount-of-cover">
                R {{ totalCover | currency }}
              </div>
              <ValidationProvider
                id="total_cover"
                v-slot="{ errors }"
                name="total_cover"
                rules="max_value:200000|min_value:50000"
              >
                <input v-model="totalCover" type="hidden" disabled />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12" class="mt-3">
              <p>
                **The total cover you can apply for is between R50 000 and R200
                000
              </p>
            </v-col>
          </v-row>
        </ValidationObserver>

        <v-row class="text-center text-sm-left">
          <v-col cols="12">
            <v-btn
              class="next-button"
              rounded
              depressed
              large
              color="primary"
              :loading="buttonLoading && !skipButtonLoading"
              @click="submit"
            >
              <span>Next</span><v-icon small>{{ icons.mdiArrowRight }}</v-icon>
            </v-btn>

            <v-btn
              :loading="skipButtonLoading"
              rounded
              depressed
              large
              color="grey"
              @click="skip()"
            >
              No thanks, I'll skip to my will
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import PeopleList from '../people/PeopleList';
  import {
    formatCurrency,
    parseCurrency,
    isNumberCheck,
  } from '../../constants/js/currency-input';

  export default {
    name: 'EstimatedInsuranceRequired',
    components: {
      PeopleList,
    },
    props: {
      testator: {
        required: true,
        type: Object,
      },
      buttonLoading: {
        default: false,
        type: Boolean,
      },
      peopleRelations: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        estateAdminExpensesCoverRequired: null,
        nominatedBeneficiaryCoverRequired: null,
        skipButtonLoading: false,
      };
    },
    computed: {
      totalCover() {
        return (
          +this.estateAdminExpensesCoverRequired +
          +this.nominatedBeneficiaryCoverRequired
        );
      },
      nominatedBeneficiaryArray() {
        return this.peopleRelations.insuranceNominatedBeneficiary.id
          ? [this.peopleRelations.insuranceNominatedBeneficiary]
          : [];
      },
      hasNominatedBeneficiaryCoverRequired() {
        return (
          this.nominatedBeneficiaryCoverRequired &&
          this.nominatedBeneficiaryCoverRequired > 0
        );
      },
      estimatedCoverRequired() {
        return this.testator.estateCostCalculation
          ? this.testator.estateCostCalculation
              .estimatedEstateAdministrativeCosts
          : null;
      },
      deletedNominatedBeneficiary() {
        return !this.hasNominatedBeneficiaryCoverRequired &&
          this.peopleRelations.insuranceNominatedBeneficiary.id
          ? this.peopleRelations.insuranceNominatedBeneficiary.id
          : null;
      },
      currencyEstateAdminExpensesCoverRequired: {
        get: function () {
          return formatCurrency(this.estateAdminExpensesCoverRequired);
        },
        set: function (newValue) {
          this.estateAdminExpensesCoverRequired = parseCurrency(newValue);
        },
      },
      currencyNominatedBeneficiaryCoverRequired: {
        get: function () {
          return formatCurrency(this.nominatedBeneficiaryCoverRequired);
        },
        set: function (newValue) {
          this.nominatedBeneficiaryCoverRequired = parseCurrency(newValue);
        },
      },
    },
    created() {
      if (this.testator.insurance) {
        this.setExistingFields();
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            const insuranceInputDetails = {
              estateAdminExpensesCoverRequired: parseInt(
                this.estateAdminExpensesCoverRequired
              ),
              nominatedBeneficiaryCoverRequired: parseInt(
                this.nominatedBeneficiaryCoverRequired
              ),
              testatorId: this.testatorId,
            };
            const params = {
              insuranceInputDetails: insuranceInputDetails,
              redirectLocation: 'InsuranceQuestions',
              deletedNominatedBeneficiary: this.deletedNominatedBeneficiary,
            };
            this.$emit('updateInsurance', params);
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      setExistingFields() {
        // using native JSON functions removes reactivity
        // so we can clone an object/variable without mutating the original source
        this.estateAdminExpensesCoverRequired = JSON.parse(
          JSON.stringify(
            this.testator.insurance.estateAdminExpensesCoverRequired
          )
        );
        this.nominatedBeneficiaryCoverRequired = JSON.parse(
          JSON.stringify(
            this.testator.insurance.nominatedBeneficiaryCoverRequired
          )
        );
      },
      isNumber(event) {
        isNumberCheck(event);
      },
      skip() {
        this.skipButtonLoading = true;
        const insuranceInputDetails = {
          testatorId: this.testatorId,
          skipInsurance: true,
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
          redirectLocation: 'OverviewPage',
        };
        this.$emit('updateInsurance', params);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .estimated-insurance-required {
    .v-text-field {
      padding: 0;
      margin: 0;
    }

    li {
      margin-bottom: 10px;
    }

    .total-amount-of-cover {
      border-radius: 10px;
      font-size: 18px;
      font-weight: bold;
      padding: 10px;
      text-align: center;
      width: 100%;
    }

    .primary-box {
      font-size: 13px;
    }

    .nominated-beneficiary {
      max-width: 450px;
    }
  }
</style>
